/**
 * 统一 AI 调用服务
 * 整合 AIAPIConfig 配置系统，支持功能开关控制
 * Minimax 使用 OpenAI 兼容 /v1/chat/completions（api.minimaxi.com），需走后端代理避免 CORS
 */

import { 
  getActiveAIConfig, 
  getAIConfigForFeature, 
  isFeatureAIEnabled,
  recordUsage 
} from '../components/AIAPIConfig';
import {
  extractMinimaxAssistantContent,
  buildMinimaxEmptyBodyHint,
  getMinimaxBusinessError,
  formatMinimaxBusinessErrorHint,
} from '../utils/minimaxResponseParse.js';
import { resolveMinimaxChatCompletionUrl } from '../utils/minimaxEndpoint.js';

// 通过后端代理调用 Minimax（OpenAI 兼容 chat/completions）
async function callMinimaxViaProxy(config, prompt, options = {}) {
  const systemContent = (options.systemPrompt || '').trim();
  const userContent = prompt.trim();
  const messages = [];
  if (systemContent) messages.push({ role: 'system', content: systemContent });
  messages.push({ role: 'user', content: userContent });

  const url = resolveMinimaxChatCompletionUrl(config.baseUrl || '');
  const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
  if (!token) {
    throw new Error('请先登录系统后再使用 AI 功能');
  }

  const maxTok = Math.min(Math.max(1, Number(options.maxTokens) || 8000), 16384);
  const requestPayload = {
    url,
    method: 'POST',
    headers: { Authorization: `Bearer ${config.apiKey}` },
    body: {
      model: config.model || 'MiniMax-M2.7',
      messages,
      temperature: options.temperature ?? 0.7,
      max_completion_tokens: maxTok,
    },
  };

  const runOnce = async () => {
    const proxyRes = await fetch('/api/proxy', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
      body: JSON.stringify(requestPayload),
    });
    const rawText = await proxyRes.text();
    let proxyJson = {};
    try {
      proxyJson = rawText ? JSON.parse(rawText) : {};
    } catch {
      if (!proxyRes.ok) throw new Error(rawText || proxyRes.statusText || '代理请求失败');
      throw new Error('代理返回格式异常');
    }
    if (!proxyRes.ok) {
      throw new Error(proxyJson?.error || rawText || proxyRes.statusText || '代理请求失败');
    }
    if (proxyJson.status !== 200) {
      const errMsg =
        proxyJson.data?.base_resp?.status_msg ||
        proxyJson.data?.error?.message ||
        `HTTP ${proxyJson.status}`;
      throw new Error(errMsg);
    }
    return proxyJson.data || {};
  };

  let data = await runOnce();
  const biz0 = getMinimaxBusinessError(data);
  if (biz0) throw new Error(formatMinimaxBusinessErrorHint(biz0));
  let content = extractMinimaxAssistantContent(data);
  const retryDelaysMs = [1000, 2200];
  for (let i = 0; (!content || !content.trim()) && i < retryDelaysMs.length; i += 1) {
    await new Promise((r) => setTimeout(r, retryDelaysMs[i]));
    data = await runOnce();
    const bizRetry = getMinimaxBusinessError(data);
    if (bizRetry) throw new Error(formatMinimaxBusinessErrorHint(bizRetry));
    content = extractMinimaxAssistantContent(data);
  }
  if (!content || !content.trim()) {
    const hint = buildMinimaxEmptyBodyHint(data);
    const keys = data && typeof data === 'object' ? Object.keys(data).slice(0, 14).join(', ') : '';
    if (hint) {
      throw new Error(
        `AI 未返回有效正文。${hint}。可尝试改写条目表述后重试；若与敏感内容无关仍反复失败，请核对 MiniMax 余额、模型名与 API Key。`,
      );
    }
    throw new Error(
      `AI返回的内容为空，请检查请求是否有效${keys ? `（响应顶层字段：${keys}；若持续出现可联系管理员核对 MiniMax 返回结构）` : ''}`,
    );
  }
  const usage = data.usage || {};
  return {
    success: true,
    content: content.trim(),
    usage: {
      promptTokens: usage.prompt_tokens ?? 0,
      completionTokens: usage.completion_tokens ?? 0,
      totalTokens: usage.total_tokens ?? usage.totalTokens ?? 0,
    },
  };
}

// 调用 OpenAI 兼容 API
async function callOpenAICompatibleAPI(config, prompt, options = {}) {
  const { baseUrl, apiKey, model } = config;
  
  // 验证 prompt 不为空
  if (!prompt || !prompt.trim()) {
    throw new Error('提示内容不能为空');
  }

  const systemContent = (options.systemPrompt || '').trim();
  const userContent = prompt.trim();
  const totalInputLen = systemContent.length + userContent.length;
  if (totalInputLen > 30000) {
    throw new Error(`输入过长（${totalInputLen} 字），超过接口上限 30720。请缩小判断范围或减少内容后重试。`);
  }

  // 构建消息
  const messages = [];
  if (systemContent) {
    messages.push({ role: 'system', content: systemContent });
  }
  messages.push({ role: 'user', content: userContent });
  
  const requestBody = {
    model: model || 'gpt-3.5-turbo',
    messages: messages,
    temperature: options.temperature || 0.7,
    max_tokens: options.maxTokens || 4000
  };
  
  const response = await fetch(`${baseUrl}/chat/completions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${apiKey}`
    },
    body: JSON.stringify(requestBody)
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorData;
    try {
      errorData = JSON.parse(errorText);
    } catch {
      errorData = { error: { message: response.statusText } };
    }
    const errorMsg = errorData.error?.message || errorData.message || response.statusText;
    console.error('[统一AI服务] API调用失败:', {
      status: response.status,
      statusText: response.statusText,
      error: errorMsg
    });
    throw new Error(errorMsg || 'API 调用失败');
  }

  const data = await response.json();
  
  // 检查响应格式
  if (!data.choices || !Array.isArray(data.choices) || data.choices.length === 0) {
    console.error('[统一AI服务] 响应格式异常:', data);
    throw new Error('API返回格式异常，未找到choices数组');
  }
  
  const content = data.choices[0]?.message?.content || '';
  
  if (!content || !content.trim()) {
    console.warn('[统一AI服务] AI返回内容为空:', data);
    throw new Error('AI返回的内容为空，请检查文档内容是否有效');
  }
  
  const usage = data.usage || {};

  return {
    success: true,
    content: content.trim(),
    usage: {
      promptTokens: usage.prompt_tokens || 0,
      completionTokens: usage.completion_tokens || 0,
      totalTokens: usage.total_tokens || 0
    }
  };
}

/**
 * 统一 AI 调用接口
 * @param {string} prompt - 用户提示
 * @param {object} options - 选项
 * @param {string} options.featureId - 功能ID（用于检查功能开关）
 * @param {string} options.systemPrompt - 系统提示
 * @param {number} options.temperature - 温度参数
 * @param {number} options.maxTokens - 最大token数
 * @returns {Promise<{success: boolean, content?: string, error?: string, usage?: object}>}
 */
export async function callUnifiedAI(prompt, options = {}) {
  const { featureId } = options;

  // 检查功能开关
  if (featureId && !isFeatureAIEnabled(featureId)) {
    return {
      success: false,
      error: `功能 "${featureId}" 的 AI 能力未启用，请在 AI 配置中启用该功能`
    };
  }

  // 获取配置
  const config = featureId 
    ? getAIConfigForFeature(featureId)
    : getActiveAIConfig();

  if (!config) {
    return {
      success: false,
      error: featureId 
        ? `功能 "${featureId}" 未配置 AI 服务，请在 AI 配置中添加服务`
        : '请先在 AI 配置中添加并设置默认服务'
    };
  }

  // 检查 API Key
  if (!config.apiKey) {
    return {
      success: false,
      error: `服务 "${config.providerId}" 的 API Key 未配置`
    };
  }

  try {
    const result = config.providerId === 'minimax'
      ? await callMinimaxViaProxy(config, prompt, options)
      : await callOpenAICompatibleAPI(config, prompt, options);

    // 记录用量
    if (result.success && result.usage) {
      recordUsage(
        config.providerId,
        config.model || 'unknown',
        result.usage.totalTokens || 0,
        featureId || 'unknown'
      );
    }

    return result;
  } catch (error) {
    console.error('AI 调用失败:', error);
    return {
      success: false,
      error: error.message || 'AI 调用失败'
    };
  }
}

/**
 * 检查功能是否已配置 AI
 * @param {string} featureId - 功能ID
 * @returns {boolean}
 */
export function isFeatureAIConfigured(featureId) {
  if (!isFeatureAIEnabled(featureId)) {
    return false;
  }
  const config = getAIConfigForFeature(featureId);
  return !!config && !!config.apiKey;
}

/**
 * 获取功能的 AI 配置状态
 * @param {string} featureId - 功能ID
 * @returns {{enabled: boolean, configured: boolean, hasConfig: boolean, error?: string}}
 */
export function getFeatureAIStatus(featureId) {
  const enabled = isFeatureAIEnabled(featureId);
  const config = enabled ? getAIConfigForFeature(featureId) : null;
  const hasConfig = !!config;
  const configured = hasConfig && !!config.apiKey;

  let error = null;
  if (!enabled) {
    error = '功能未启用';
  } else if (!hasConfig) {
    error = '未配置 AI 服务';
  } else if (!configured) {
    error = 'API Key 未配置';
  }

  return {
    enabled,
    configured,
    hasConfig,
    error
  };
}

/**
 * 获取当前活动的 AI 配置
 * @returns {object|null}
 */
export function getActiveConfig() {
  return getActiveAIConfig();
}

/**
 * 检查是否有可用的 AI 配置
 * @returns {boolean}
 */
export function hasAIConfig() {
  const config = getActiveAIConfig();
  return !!config && !!config.apiKey;
}

export default {
  callUnifiedAI,
  isFeatureAIConfigured,
  getFeatureAIStatus,
  getActiveConfig,
  hasAIConfig
};
